/**
 * Activities across every program (M1 track D4).
 *
 * The cross-program view: "what is happening this month", rather than "what is
 * in this program". Creation is deliberately not here — an activity needs a
 * program (§7.13), and it is created on the program screen where that is
 * unambiguous.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTIVITY_STATUSES, formatDuration } from '@parinaam-vms/shared';
import type { ActivityWithProgram } from '@parinaam-vms/shared';
import {
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  Pager,
  SelectInput,
  StatusBadge,
  TextInput,
} from '../../components/common';
import { CRUMB } from '../../constants/breadcrumbs';
import { ROWS_PER_PAGE } from '../../constants/pagination';
import { useActivities } from '../../hooks/usePrograms';
import {
  countedTitle,
  formatDateRange,
  formatTimeRange,
  humaniseCode,
  orDash,
} from '../../utils/format';

const STATUS_OPTIONS = ACTIVITY_STATUSES.map((status) => ({
  value: status,
  label: humaniseCode(status),
}));

export function ActivitiesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const query = useActivities({
    search: search.trim() || undefined,
    status: status || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    page_size: ROWS_PER_PAGE,
  });

  const rows = query.data?.items ?? [];

  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader title="Activities" breadcrumbs={[CRUMB.activities]} />

      <Card title={countedTitle('Activities', query.data?.pagination?.total)}>
        <FilterBar label="Filter activities">
          <TextInput
            label="Search"
            placeholder="Activity, program or location"
            value={search}
            onChange={(event) => change(setSearch)(event.target.value)}
          />
          <SelectInput
            label="Status"
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => change(setStatus)(event.target.value)}
          />
          <TextInput
            label="From"
            type="date"
            value={from}
            onChange={(event) => change(setFrom)(event.target.value)}
          />
          <TextInput
            label="To"
            type="date"
            value={to}
            onChange={(event) => change(setTo)(event.target.value)}
          />
        </FilterBar>

        {query.isPending ? (
          <LoadingState label="Loading activities…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No activities found"
            message={
              search || status || from || to
                ? 'No activity matches these filters.'
                : 'Open a program to create its first activity.'
            }
          />
        ) : (
          <>
            <Pager pagination={query.data?.pagination} onPageChange={setPage} />
            <DataTable<ActivityWithProgram>
              caption="Activities"
              rows={rows}
              getRowKey={(row) => row.activity_id}
              // The trail on the other side is built from this: without it the
              // activity screen sends people back to Programs, skipping the
              // stage they actually came through.
              onRowClick={(row) =>
                navigate(`/activities/${row.activity_id}`, { state: { from: 'activities' } })
              }
              columns={[
                { key: 'name', header: 'Activity name', render: (row) => row.name },
                {
                  key: 'when',
                  header: 'Activity date',
                  render: (row) => formatDateRange(row.start_date, row.end_date),
                },
                {
                  key: 'time',
                  header: 'Activity time',
                  render: (row) => formatTimeRange(row.start_time, row.end_time),
                },
                {
                  key: 'location',
                  header: 'Activity location',
                  render: (row) => orDash(row.location),
                },
                { key: 'program', header: 'Activity program', render: (row) => row.program_name },
                {
                  key: 'hours',
                  header: 'Number of hours per activity',
                  numeric: true,
                  render: (row) =>
                    row.planned_minutes == null ? '—' : formatDuration(row.planned_minutes),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (row) => <StatusBadge status={row.status} />,
                },
              ]}
            />
          </>
        )}
      </Card>
    </>
  );
}
